import {FigureMainClass} from "./figureMainClass";
import {ObjectConfig} from "../../models/ObjectConfig";
import {MapPosition} from "../../models/MapPosition";
import * as turf from "@turf/turf";
import {asyncScheduler, BehaviorSubject, from, Observable, Subject, Subscription, timer} from 'rxjs';
import {isRemoveSidebar} from '@src/configs/view_only';
import {debounceTime, filter, take, throttleTime} from "rxjs/operators";

export class Polyline extends FigureMainClass{
  private _path : MapPosition[];
  private _width : number;
  private _width_px : number;
  private _height : number;
  private _color : string;
  private _dashWidthInMeters : number;
  private _weight : number;
  private _opacity : number;
  private isVisible: boolean = true;
  private isTextVisible: boolean = true;
  private subscriptions: Subscription;
  private _importantOpacity: number;


  private dragStartPosition:MapPosition[] = null;
  private isObjectHighlighted: boolean;
  private isEditing: boolean;
  private editingSubscriber = null;

  private localDragStart$ = new BehaviorSubject(false);


  get path() { return this.normalizePath(this._path); }
  set path(val) {
    this._path = val;
    this.updateLinkedObjectsAnchorPosition();
    this.debounceObservable$.pipe(debounceTime(300)).subscribe(() => {
      this.setObjectConfigValue('latLngs', this.path);
    });
  }
  get widthPx() { return this._width_px; }
  set widthPx(val) { this._width_px = (val > 32) ? 32 :val; }
  get width() { return this._width; }
  set width(val) { this._width = val; }
  get height() { return this._height; }
  set height(val) { this._height = val; }
  get color() { return this._color; }
  set color(val) { this._color = val; }
  get dashWidthInMeters() {
    return this._dashWidthInMeters;
  }
  set dashWidthInMeters(val) { this._dashWidthInMeters = val; }
  get weight() { return this._weight; }
  set weight(val) { this._weight = val; }
  get opacity() { return this._opacity; }
  set opacity(val) { this._opacity = val; }
  get importantOpacity() { return this._importantOpacity; }
  // set importantOpacity(val) { this._importantOpacity = val; }

  get pathAsArrays() { return this.path.map((mp : MapPosition) => mp.asArray()); }

  __init(config: ObjectConfig) {
    this.subscriptions = new Subscription();
    this.__updateMainPropertiesFromConfig(config);

    this.initSelfListeners();
  }

  private __updateMainPropertiesFromConfig(config:ObjectConfig){
    this._path = config.getPositions();
    this._width = config.getFixedWidth();
    this.widthPx = config.getFixedWidthPx();
    this._height = config.getFixedHeight();
    this._color = config.getStrokeColor();
    this._dashWidthInMeters = config.getDashWidthInMeters();
    this._weight = config.getStrokeWeight();
    this._opacity = (this.isSvgLine() || this.hasTextAlongPolyline()) && this.tool.default_opacity == 0 ? 0 : config.getFillOpacity();
    this._importantOpacity = (this.isSvgLine() || this.hasTextAlongPolyline()) && this.tool.default_opacity == 0 ? 0 : config.getImportantFillOpacity();
  }

  private normalizePath(path: MapPosition[]): MapPosition[] {
    if (path.length > 1) {
      return path;
    }
    if (path.length == 1) {
      return [path[0], path[0]];
    }
    throw new Error('Empty path for Polyline');
  }

  private initSelfListeners(){
    if (!isRemoveSidebar) {
      // let isMoving = false;
      this.subscriptions.add(this.view.zoom.subscribe(()=>{
        if (this.isVisible && this.isTextVisible) {
          if (this.hasTextAlongPolyline()) {
            if (!this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
              this.view.entityTextToBeRepositioned.set(this.viewEntityId, this);
            }
          }
          this.showSvgLine();
        }
      }));

      this.subscriptions.add(this.view.removeDragHighlight$.pipe(filter( ({id}) => id === this.viewEntityId && this.localDragStart$.getValue()))
        .subscribe((id) => {
          this.view.removeObject(this.viewEntityId);

          this.view.drawPolyline(this.tool, this, (entityId) => {
            this.viewEntityId = entityId;

            if (!this.isObjectHighlighted) {
              this.showPolylineText();
              this.showSvgLine();
            } else {
              this.setObjectHighlight(true);
            }

            if (this.tool.has_smoothing) {
              this.view.smoothPolyline(this.viewEntityId);
            }
          });
        }))

      this.subscriptions.add(this.view.sceneRotated$.pipe(filter(val => this.isVisible && this.isTextVisible)).subscribe(()=>{
          if (this.hasTextAlongPolyline()) {
            if (!this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
              this.view.entityTextToBeRepositioned.set(this.viewEntityId, this);
            }
          }
          this.showSvgLine();
      }))

      this.subscriptions.add(this.view.cameraEventMove.subscribe((event) => {
        if (this.isVisible && this.isTextVisible) {
          if (this.hasTextAlongPolyline()) {
            if (!this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
              this.view.entityTextToBeRepositioned.set(this.viewEntityId, this);
            }
          }
          this.showSvgLine();
        }
      }));
    }
    if (!isRemoveSidebar) {
      this.subscriptions.add(this.view.isThreeDMode.subscribe((val) => {
        this.view.setPolylineClampToGround(val, this.viewEntityId)
      }));
    }
  }

  public setVisibility(isVisible) {
    if (this.viewEntityId) {
      this.view.setVisibility(this.viewEntityId, isVisible);
      this.isVisible = isVisible;
      // if (this.hasTextAlongPolyline()) {
      //   if (!isVisible) {
      //     this.view.removePolylineText(this.viewEntityId);
      //   } else {
      //     this.view.drawPolylineText(this.getToolWithAdditionalParams(), this.path, this.getViewEntityId(), () => {});
      //   }
      // }
      if (this.isSvgLine()) {
        if (isVisible) {
          this.view.drawPolylineSvgLine(this.tool, this.path, this.getViewEntityId(), () => {
          });
        } else {
          this.view.removePolylineSvgLine(this.viewEntityId);
        }
      }

      if (isVisible) {
        this.showPolylineText();
      } else {
        this.hidePolylineText();
      }
    }
  }

  public setObjectHighlight(flag, showNumbers = false) {
    const prevHL = this.isObjectHighlighted;
    this.isObjectHighlighted = flag;
    if (flag && !prevHL) {
      this.hidePolylineText();
      this.hideSvgLine();
    }
    if (!flag && prevHL) {
      this.showSvgLine();
      this.showPolylineText();
    }
    this.view.highlightPolyline(this.viewEntityId, flag, showNumbers);
  }

  private checkForLineTypeWithSvg(){
    return this.tool.line_type == 'dashed';
  }

  private hasTextAlongPolyline(){
    const isHasSeparateOffsetAndRepeat = this.isToolHasOffsetAndRepeat();
    let isHasSvgOffsetAndRepeat = false;
    if (this.tool.icon_config_svg && this.tool.icon_config_svg.length > 0) {
      isHasSvgOffsetAndRepeat = this.tool.icon_config_svg.includes('offset')
          && this.tool.icon_config_svg.includes('repeat');
    }

    return this.tool.icon_config_svg
        && this.tool.icon_config_svg.length > 0
        && (isHasSeparateOffsetAndRepeat || isHasSvgOffsetAndRepeat);
  }

  private isToolHasOffsetAndRepeat(){
    let result = typeof this.tool.offset == "number" && this.tool.repeat;
    if (!result && this.tool.icon_config_svg){
      let icon_config = null;
      eval('icon_config = ' + this.tool.icon_config_svg);

      if (Array.isArray(icon_config)){
        return icon_config[0] && icon_config[0].offset.toString().length > 0 && icon_config[0].repeat.toString().length > 0;
      }
      else{
        return icon_config && (icon_config.offset && icon_config.offset.toString().length > 0) && (icon_config.repeat && icon_config.repeat.toString().length > 0);
      }
    }
    return result;
  }

  private isSvgLine() {
    return ['dashed', 'dotdash', 'dotted', 'longdash', 'twodash'].indexOf(this.tool.line_type) >= 0 && !this.tool.has_smoothing;
  }


  protected __onServerUpdate(config) {
    let configPositions = config.getPositions();
    let changed = false;
    let toolChanged = false;

    if (this.tool.id != config.getTool().id){
      this.removeObject(true);
      this.tool = config.getTool();
      toolChanged = true;
    }

    if (configPositions.length != this.path.length) {
      changed = true;
    }
    if (!changed) {
      for (let i = 0; i < this.path.length; i++) {
        if (!this.path[i].equals(configPositions[i])) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      this._path = configPositions;
    }
    if (this.width != config.getFixedWidth()) {
      changed = true;
      this._width = config.getFixedWidth();
    }
    if (this.height != config.getFixedHeight()) {
      changed = true;
      this._height = config.getFixedHeight();
    }
    if (this.widthPx != config.getFixedWidthPx()) {
      this.widthPx = config.getFixedWidthPx();
      changed = true;
    }
    // return;

    if (this.dashWidthInMeters != config.getDashWidthInMeters()){
      changed = true;
      this.dashWidthInMeters = config.getDashWidthInMeters();
    }

    if (toolChanged){
      // this.removeObject(true);
      this.__updateMainPropertiesFromConfig(config);
      this.draw().subscribe((entityId)=>{
        this.showPolylineText();
        this.showSvgLine();
      });
    }
    else{
      if (changed) {
        this.redraw();
      }
    }
  }

  protected __onInternalUpdate(config) {
    let changed = false;
    let isFullRedraw = false;

    if (this.color != config.getStrokeColor()){
      isFullRedraw = true;
      this._color = config.getStrokeColor();
    }

    if (this.opacity != config.getFillOpacity()){
      isFullRedraw = true;
      this._importantOpacity = (this.isSvgLine() || this.hasTextAlongPolyline()) && this.tool.default_opacity == 0 ? 0 : config.getFillOpacity();
      this._opacity = (this.isSvgLine() || this.hasTextAlongPolyline()) && this.tool.default_opacity == 0 ? 0 : config.getFillOpacity();
    }

    if (this.width != config.getFixedWidth()) {
      this._width = config.getFixedWidth();
      if (!this.widthPx){
        changed = true;
      }
    }

    if (this._width_px != config.getFixedWidthPx()) {
      changed = true;
      this.widthPx = config.getFixedWidthPx();
    }

    if (this.height != config.getFixedHeight()) {
      changed = true;
      this._height = config.getFixedHeight();
    }

    // return;
    if (this.dashWidthInMeters != config.getDashWidthInMeters()){
      changed = true;
      this.dashWidthInMeters = config.getDashWidthInMeters();
    }

    console.log('isFullRedraw', isFullRedraw);
    console.log('this.opacity', this.opacity);

    if (isFullRedraw){
      this.removeObject(true);
      this.__updateMainPropertiesFromConfig(config);
      this.draw().subscribe((entityId)=>{
        this.showPolylineText();
        this.showSvgLine();
      });
    }
    else{
      if (changed){
        this.redraw();
      }
    }
  }

  private removeLines() {
    if (this.hasTextAlongPolyline()){
      this.view.removePolylineText(this.viewEntityId);
    }

    if (this.isSvgLine()){
      this.view.removePolylineSvgLine(this.viewEntityId);
    }

    if (this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
      this.view.entityTextToBeRepositioned.delete(this.viewEntityId);
    }

    if (this.view.entitySvgToBeRepositioned.has(this.viewEntityId)){
      this.view.entitySvgToBeRepositioned.delete(this.viewEntityId);
    }
    this.isTextVisible = false;
  }

  public removeObject(withoutUnsubscribe?){
    this.removeLines();

    if (!withoutUnsubscribe){
      this.subscriptions.unsubscribe();
    }
    super.removeObject();
  }

  public draw() {
    return from(new Promise((resolve, reject) => {
      return this.view.drawPolyline(
        this.tool,
        this,
        (entityId) => {
          if (this.tool.addItemMapping){
            this.tool.addItemMapping(this.itemId, entityId);
          }
          if (this.isSvgLine()) {
            this.view.removePolylineSvgLine(this.viewEntityId);
          }
          if (this.hasTextAlongPolyline()) {
            this.view.removePolylineText(this.viewEntityId);
          }
          this.setViewEntityId(entityId);
          if (this.isSvgLine()) {
            this.view.removePolylineSvgLine(this.viewEntityId);
            this.view.drawPolylineSvgLine(this.tool, this.path, entityId, () => {
            });
          }
          if (this.tool.has_smoothing){
            this.view.smoothPolyline(this.viewEntityId);
          }
          if (this.hasTextAlongPolyline()) {
            this.view.removePolylineText(this.viewEntityId);
            const toolCopy = this.getToolWithAdditionalParams();
            this.view.drawPolylineText(toolCopy, this.path, entityId, (args) => {
              resolve(entityId);
            });
          } else {
            resolve(entityId);
          }
        }
      );
    }));
  }

  public startDrag() {
    this.localDragStart$.next(true);
    this.hidePolylineText();
    this.hideSvgLine();
    this.removeObject(this.viewEntityId);
    this.dragStartPosition = this.path.slice();
    this.view.drawingTool.drawCorridor(this.path, {tool: this.tool, color: '#EB6432'})
        .then((id) => {
          this.viewEntityId = id;
          let path = this.path;
          if (this.tool.has_smoothing) {

            path = this.view.getSmoothPolylinePos(this.viewEntityId).map(p => new MapPosition(p));
          }
          this.view.startDragPolyline(this.viewEntityId, path, this.tool);
        });
  }

  public onDragEnd(object: {position, id}) {
    this.localDragStart$.next(false);
    this.view.removeObject(this.viewEntityId);

    if (Math.abs(object.position[0].latitude - this.path[0].lat) > 0.00000001
        || Math.abs(object.position[0].longitude - this.path[0].lng) > 0.00000001) {
      if (this.tool.has_smoothing){
        const dragEndPosition = new MapPosition(object.position[0]);
        const difference = {lat: dragEndPosition.lat - this.dragStartPosition[0].lat, lng: dragEndPosition.lng - this.dragStartPosition[0].lng};
        object.position = this.path.map(p => new MapPosition({lat: p.lat+difference.lat, lng: p.lng+difference.lng, height: dragEndPosition.height}));
      }
      this.path = object.position;
    }

    this.view.drawPolyline(this.tool, this, (entityId) => {
      this.viewEntityId = entityId;
      this._dragEnd.next({oldPosition: null, object:object});

      if (!this.isObjectHighlighted) {
        this.showPolylineText();
        this.showSvgLine();
      } else {
        this.setObjectHighlight(true);
      }

      if (this.tool.has_smoothing) {
        this.view.smoothPolyline(entityId);
      }
    });

    // this.setObjectConfigValue('positions', object.position);
  }

  public stopEdit() {
    if (this.isEditing) {
      this.view.removeObject(this.viewEntityId);
      this.removeLines();
      this.draw().pipe(take(1)).subscribe((entityId) => {
        this.viewEntityId = entityId;
        this.isEditing = false;

        this.showPolylineText();
        this.showSvgLine();

        if (this.tool.has_smoothing) {
          this.view.smoothPolyline(entityId);
        }

        if (this.editingSubscriber) {
          this.editingSubscriber.unsubscribe();
        }
        this.editStop.next(1);
      });
    } else {
      super.stopEdit();
    }
  }

  public startEdit(withNumbers = false) {
    this.isEditing = true;
    this.view.hideHoverText();

    this.view.removeObject(this.viewEntityId);
    this.removeLines();

    this.view.drawingTool.drawCorridor(this.path, {tool: this.tool, color: '#EB6432', width: 0.5}, null, this.view.getMaxOverlayHeights() + 2)
        .then((id) => {
          this.viewEntityId = id;
          this.view.makeSvgNonInteractive(this.viewEntityId);
          this.view.editPolyline(this, (entity, positions) => {
            this.path = positions;
            this.stopEdit();
          }, (index)=>{
            // this action adds new recrods to undo log during edit... it seems to be wrong behavior.
            // this._pointAdded$.next(index);
          }, withNumbers);
        });
  }

  public rotate(degrees){
    this.path = turf
      .transformRotate(turf.lineString(this.pathAsArrays), degrees)
      .geometry.coordinates.map(MapPosition.fromArray);

    return new Promise((resolve)=>{
      this.redraw(resolve);
    });
  }

  public moveBy(offset) {
    let positions = this.path.slice(0);
    positions.map((pos:MapPosition)=>{
      if (typeof pos.longitude == 'string'){
        pos.longitude = parseFloat(pos.longitude);
        pos.latitude = parseFloat(pos.latitude);
      }
      pos.longitude+=offset.longitude;
      pos.latitude+=offset.latitude;
    });

    this.path = positions;
    this.redraw();
  }

  moveByOffsetFromOriginalPosition(offset){
    let positions = this.path.slice(0);
    positions = positions.map((pos:MapPosition)=>{
      return new MapPosition({lng: pos.longitude+offset.longitude, lat: pos.latitude+offset.latitude});
    });
    this.lastDragPosition = positions;

    this.redrawWithOptionsWithoutSave(positions)
  }

  updateObjectPositionByMultiSelect() {
    if (this.lastDragPosition) {
      this.path = this.lastDragPosition;
      this.lastDragPosition = null;
    }
  }

  public moveWithDirection(event){
    let positions = this.path.slice(0);
    this.path = positions.map((pos)=>{
      return this.view.getPositionAfterDirectionOffset(pos, event);
    });

    this.redraw(null, true);
  }

  public centerOnObject(){
    this.view.centerOnEntity(this.viewEntityId, this.path[0]);
  }

  public getLinkedObjectsAnchorPosition() {
    return this.path[0];
  }

  redrawWithOptionsWithoutSave(positions){
    this.hidePolylineText();
    this.view.movePolylineToPath(this.getViewEntityId(), positions);
  }


  public redraw(cb?, dontShowSvg?) {
    this.hidePolylineText();
    // note: [SHR]: difference
    // this.view.redrawPolyline(this, () => {
    //   if (!dontShowSvg){
    //     this.showPolylineText();
    //     this.showSvgLine();
    //   }
    //   if (cb) { cb(); }
    // });

    if (!this.viewEntityId){
      return timer(400).subscribe(()=>{
        this.redraw(cb);
      });
    }
      this.view.redrawPolyline(this, () => {
        this.showPolylineText();
        this.showSvgLine();
        if (cb) { cb(); }
      });
  }

  protected hidePolylineText() {
    if (this.hasTextAlongPolyline()){
      this.view.removePolylineText(this.viewEntityId);
      if (this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
        this.view.entityTextToBeRepositioned.delete(this.viewEntityId);
      }
      this.isTextVisible = false;
    }
  }

  protected hideSvgLine() {
    if (this.isSvgLine()){
      this.view.removePolylineSvgLine(this.viewEntityId);
      if (this.view.entitySvgToBeRepositioned.has(this.viewEntityId)) {
        this.view.entitySvgToBeRepositioned.delete(this.viewEntityId);
      }
    }
    this.isTextVisible = false;
  }

  public getToolWithAdditionalParams() {
    let tool = Object.assign({}, this.tool);
    this.toolService.assignMappings(tool);
    tool.dashWidthInMeters = this.dashWidthInMeters;
    return tool;
  }

  protected showPolylineText(path?) {
    if (this.hasTextAlongPolyline()) {
      this.view.drawPolylineText(
          this.getToolWithAdditionalParams(),
          (path) ? path : this.path,
          this.viewEntityId,
          (args) => {}
      );
      if (!this.view.entityTextToBeRepositioned.has(this.viewEntityId)) {
        this.view.entityTextToBeRepositioned.set(this.viewEntityId, this);
      }
      this.isTextVisible = true;
    }
  }

  protected showSvgLine(){
    if (this.isSvgLine() && !this.tool.has_smoothing){
      if (!this.view.entitySvgToBeRepositioned.has(this.viewEntityId)) {
        this.view.entitySvgToBeRepositioned.set(this.viewEntityId, this);
      }
    }
    this.isTextVisible = true;
  }
}
